import { Check, Command, Info, Lock, X, XCircle, Zap } from "lucide-react";
import { useMemo, useState } from "react";

import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { ACCESS_CATALOG } from "../../constants/accessCatalog.constants";
import { useApiKeysData, useRolesData, useUsersData } from "../../hooks/useAccessData";
import type { AtomicAction, GatheredStatement } from "../../types/access.types";
import { gatherStatements, resolveCell, summarize } from "../../utils/denyFirst";
import { roleColor } from "../../utils/roleColor";
import { SettingsSection } from "../SettingsSection";
import { PrincipalRolesCell } from "./PrincipalRolesCell";

interface SimPrincipal {
  id: string;
  name: string;
  kind: "person" | "key";
  roleIds: string[];
}

interface Probe {
  resource: string;
  action: AtomicAction;
}

/** Dedupe gathered statements by their originating role + policy. */
function uniqueByRolePolicy(statements: readonly GatheredStatement[]): GatheredStatement[] {
  const seen = new Set<string>();
  const out: GatheredStatement[] = [];
  for (const s of statements) {
    const key = `${s.role.id}:${s.policy.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

/**
 * "Simulator" section of Access & permissions. Pick a principal (user or API key) and a permission to see
 * _why_ access is granted or denied — which role and policy grant it, whether a deny overrides an allow, or
 * whether it falls to default-deny. Pure read/compute: it reuses the same deny-first engine and the
 * already-loaded principals, roles and policies. No new endpoints.
 */
export function SimulatorSection() {
  const { t } = useTranslations("settings");

  const { roles, isLoading: rolesLoading, hasNoAccess: rolesNoAccess } = useRolesData();
  const {
    users,
    rolesByUser,
    rolesUnavailable: userRolesUnavailable,
    isLoading: usersLoading,
    hasNoAccess: usersNoAccess,
  } = useUsersData();
  const {
    keys,
    rolesByKey,
    rolesUnavailable: keyRolesUnavailable,
    isLoading: keysLoading,
    hasNoAccess: keysNoAccess,
  } = useApiKeysData();

  const [selectedId, setSelectedId] = useState("");
  const [probe, setProbe] = useState<Probe | null>(null);

  // Principals whose per-principal roles fetch failed are EXCLUDED (with a visible note below), not listed:
  // simulating them with an empty role set would present "NO ROLES · 0/N" as authoritative when their real
  // access is simply unknown.
  const excludedCount = userRolesUnavailable.size + keyRolesUnavailable.size;

  const principals = useMemo<SimPrincipal[]>(() => {
    const list: SimPrincipal[] = [];
    for (const u of users) {
      if (userRolesUnavailable.has(u.id)) continue;
      list.push({
        id: u.id,
        name: u.full_name,
        kind: "person",
        roleIds: (rolesByUser[u.id] ?? []).map((r) => r.id),
      });
    }
    for (const k of keys) {
      if (keyRolesUnavailable.has(k.id)) continue;
      list.push({
        id: k.id,
        name: k.name,
        kind: "key",
        roleIds: (rolesByKey[k.id] ?? []).map((r) => r.id),
      });
    }
    return list;
  }, [users, rolesByUser, userRolesUnavailable, keys, rolesByKey, keyRolesUnavailable]);

  const isLoading = rolesLoading || usersLoading || keysLoading;

  // Same no-access treatment as the other sections: without the roles catalog nothing can be resolved, and
  // without ANY principal source there is nothing to simulate for permission reasons — both are "no access",
  // not the generic "no principals" empty state.
  const hasNoAccess = rolesNoAccess || (usersNoAccess && keysNoAccess);

  // Derived active principal. The explicit selection wins; falling back to the first only when NOTHING is
  // selected yet.
  //
  // The `selectedId && !found` case is deliberately null rather than `principals[0]`: the users list is now
  // one page of a server-side query, so paginating or filtering the Usuarios table replaces it under this
  // section. Silently re-pointing the simulator at whoever happens to lead the new page would answer a
  // question about a different person than the one on screen — on a permissions screen, the worst kind of
  // wrong. Rendering nothing, with the note below, is honest.
  const selected = selectedId ? (principals.find((p) => p.id === selectedId) ?? null) : null;
  const principal = selectedId ? selected : (principals[0] ?? null);
  const selectionDropped = !!selectedId && selected === null;

  // Assigned roles resolved against the catalog for the header pills (unresolvable ids are skipped —
  // same as gatherStatements, so the pills always match what the matrix actually evaluates).
  const principalRoles = useMemo(
    () => (principal ? principal.roleIds.flatMap((rid) => roles.find((r) => r.id === rid) ?? []) : []),
    [principal, roles],
  );

  const statements = useMemo(
    () => (principal ? gatherStatements(principal.roleIds, roles) : []),
    [principal, roles],
  );
  const summary = useMemo(() => summarize(statements), [statements]);
  const result = probe ? resolveCell(statements, probe.resource, probe.action) : null;

  const principalOptions = useMemo(
    () =>
      principals.map((p) => ({
        value: p.id,
        label: p.kind === "key" ? `${p.name} · ${t("access_badge_api_key")}` : p.name,
      })),
    [principals, t],
  );

  return (
    <SettingsSection
      id="simulador"
      eyebrow={t("access_eyebrow")}
      title={t("access_sim_title")}
      sub={t("access_sim_sub")}
    >
      {hasNoAccess ? (
        <ColocaEmpty
          surface="card"
          icon={Lock}
          title={t("access_no_access_title")}
          description={t("access_no_access_description")}
        />
      ) : isLoading ? (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <ColocaSkeleton className="h-64 w-full" />
          <ColocaSkeleton className="h-64 w-full" />
        </div>
      ) : principals.length === 0 ? (
        <ColocaEmpty surface="card" icon={Command} title={t("access_sim_no_principals")} />
      ) : (
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
          {/* Left: principal picker + matrix */}
          <div className="overflow-hidden rounded-md border border-border">
            <div className="border-b border-border bg-muted/40 p-3.5">
              <label
                htmlFor="sim-principal"
                className="mb-1 block font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground"
              >
                {t("access_sim_principal_label")}
              </label>
              <ColocaSelect
                id="sim-principal"
                value={principal?.id ?? ""}
                onValueChange={(v) => {
                  setSelectedId(v);
                  setProbe(null);
                }}
                options={principalOptions}
                placeholder={t("access_sim_principal_placeholder")}
              />
              {excludedCount > 0 && (
                <div className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
                  <Info size={12} className="mt-0.5 shrink-0" />
                  {t("access_sim_partial_principals", { count: excludedCount })}
                </div>
              )}
              {/* The picker is fed by the Usuarios table's current page, which is now a server-side query.
                  Say so, rather than letting an admin conclude that someone who is simply on another page
                  has no access. */}
              {selectionDropped && (
                <div className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-warning">
                  <Info size={12} className="mt-0.5 shrink-0" />
                  {t("access_sim_selection_dropped")}
                </div>
              )}
              <div className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
                <Info size={12} className="mt-0.5 shrink-0" />
                {t("access_sim_page_scope_note", { count: principals.length })}
              </div>
              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                {/* Same capped roles cell as the Usuarios / API Keys tables — a principal with dozens of
                    roles must not blow up the picker header. No drawer here, so the "+N" chip is static.
                    rolesFailed is always false by construction: principals whose roles fetch failed are
                    excluded from `principals` upstream. */}
                {principal && <PrincipalRolesCell roles={principalRoles} rolesFailed={false} />}
                <span className="ml-auto font-mono text-[11px] text-muted-foreground">
                  {summary.allowed}
                  <span className="text-muted-foreground/60">/{summary.total}</span>
                  {summary.denied > 0 && (
                    <span className="ml-1.5 text-destructive">·{summary.denied} deny</span>
                  )}
                </span>
              </div>
            </div>
            <div className="max-h-[520px] overflow-auto p-3">
              <div className="mb-2.5 font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
                {t("access_sim_matrix_hint")}
              </div>
              <div className="flex flex-col gap-2">
                {ACCESS_CATALOG.map((mod) => {
                  const ModIcon = mod.icon;
                  return (
                    <div key={mod.id} className="flex items-center gap-2">
                      <div className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded border border-border bg-muted text-muted-foreground">
                        <ModIcon size={12} />
                      </div>
                      {/* Narrower on a phone: at 140px the module name leaves ~62px for the chip lane,
                          under the width of a single chip, so all three stacked one per line for every
                          one of the 27 modules. The name still truncates, the chips stay usable. */}
                      <span
                        className="w-[110px] shrink-0 truncate text-[11.5px] font-medium text-foreground sm:w-[140px]"
                        title={mod.id}
                      >
                        {t(mod.labelKey)}
                      </span>
                      <div className="ml-auto flex flex-wrap justify-end gap-1">
                        {mod.actions.map((atom) => {
                          const cell = resolveCell(statements, mod.id, atom);
                          const active = probe?.resource === mod.id && probe.action === atom;
                          const denyWins = cell.effect === "deny" && cell.overridden.length > 0;
                          return (
                            <button
                              key={atom}
                              type="button"
                              aria-pressed={active}
                              onClick={() => setProbe({ resource: mod.id, action: atom })}
                              className={cn(
                                "inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 font-mono text-[10px] transition-shadow",
                                cell.effect === "allow" && "border-primary/30 bg-primary/10 text-primary",
                                cell.effect === "deny" &&
                                  "border-destructive/40 bg-destructive/10 text-destructive",
                                cell.effect === "none" && "border-border text-muted-foreground/60",
                                active && "ring-2 ring-ring/50",
                              )}
                            >
                              {cell.effect === "allow" && <Check size={9} />}
                              {cell.effect === "deny" && <X size={9} />}
                              {atom}
                              {denyWins ? "*" : ""}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right: explanation */}
          <div className="overflow-hidden rounded-md border border-border lg:sticky lg:top-2">
            <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-3.5 py-2.5">
              <Zap size={13} className="text-primary" />
              <span className="text-[12.5px] font-semibold text-foreground">
                {t("access_sim_explanation_title")}
              </span>
            </div>
            <div className="p-4">
              {!probe || !result ? (
                <div className="px-2 py-10 text-center text-muted-foreground">
                  <Command size={22} className="mx-auto mb-2.5 text-muted-foreground/50" />
                  <div className="text-[13px]">{t("access_sim_pick_prompt")}</div>
                </div>
              ) : (
                <Explanation probe={probe} result={result} principalName={principal?.name ?? ""} />
              )}
            </div>
          </div>
        </div>
      )}
    </SettingsSection>
  );
}

interface ExplanationProps {
  probe: Probe;
  result: ReturnType<typeof resolveCell>;
  principalName: string;
}

function Explanation({ probe, result, principalName }: ExplanationProps) {
  const { t } = useTranslations("settings");
  const label = `${probe.resource}:${probe.action}`;
  const overriddenCount = result.overridden.length;

  const headline =
    result.effect === "allow"
      ? {
          text: t("access_sim_result_allow"),
          cls: "text-primary border-primary/30 bg-primary/10",
          Icon: Check,
        }
      : result.effect === "deny"
        ? {
            text: t("access_sim_result_deny"),
            cls: "text-destructive border-destructive/40 bg-destructive/10",
            Icon: XCircle,
          }
        : {
            text: t("access_sim_result_none"),
            cls: "text-muted-foreground border-border bg-muted/40",
            Icon: X,
          };
  const HeadIcon = headline.Icon;

  const subline =
    result.effect === "deny" && overriddenCount > 0
      ? t("access_sim_summary_deny_wins", { count: overriddenCount })
      : result.effect === "allow"
        ? t("access_sim_summary_allow")
        : result.effect === "deny"
          ? t("access_sim_summary_deny")
          : t("access_sim_summary_none");

  return (
    <div>
      <div className="mb-3.5 flex items-center gap-2">
        <span className="rounded border border-border bg-muted/60 px-2 py-0.5 font-mono text-[12.5px] font-semibold text-foreground">
          {label}
        </span>
      </div>

      <div className={cn("mb-4 flex items-center gap-2.5 rounded-md border px-3.5 py-3", headline.cls)}>
        <HeadIcon size={20} />
        <div>
          <div className="font-mono text-[13px] font-bold tracking-[0.03em]">{headline.text}</div>
          <div className="text-[11.5px] text-muted-foreground">{subline}</div>
        </div>
      </div>

      {result.effect === "none" ? (
        <p className="text-[12.5px] leading-relaxed text-muted-foreground">
          {t("access_sim_none_detail", { name: principalName, perm: label })}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {result.effect === "deny" && (
            <ReasonBlock
              effect="deny"
              title={t("access_sim_reason_denied_by")}
              items={uniqueByRolePolicy(result.by)}
            />
          )}
          {overriddenCount > 0 && (
            <ReasonBlock
              effect="allow"
              muted
              title={t("access_sim_reason_overridden")}
              items={uniqueByRolePolicy(result.overridden)}
            />
          )}
          {result.effect === "allow" && (
            <ReasonBlock
              effect="allow"
              title={t("access_sim_reason_allowed_by")}
              items={uniqueByRolePolicy(result.by)}
            />
          )}
        </div>
      )}

      <div className="mt-4 rounded-md border border-dashed border-border bg-muted/30 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
        {t("access_sim_rule")}
      </div>
    </div>
  );
}

interface ReasonBlockProps {
  effect: "allow" | "deny";
  title: string;
  items: readonly GatheredStatement[];
  muted?: boolean;
}

function ReasonBlock({ effect, title, items, muted }: ReasonBlockProps) {
  const { t } = useTranslations("settings");
  const isDeny = effect === "deny";

  return (
    <div className={cn(muted && "opacity-70")}>
      <div
        className={cn(
          "mb-1.5 font-mono text-[10px] uppercase tracking-[0.06em]",
          muted ? "text-muted-foreground/70" : isDeny ? "text-destructive" : "text-primary",
        )}
      >
        {title}
      </div>
      <div className="flex flex-col gap-1.5">
        {items.map((s) => (
          <div
            key={`${s.role.id}:${s.policy.id}`}
            className={cn(
              "flex items-center gap-2 rounded-md border px-2.5 py-2",
              muted
                ? "border-border bg-card line-through"
                : isDeny
                  ? "border-destructive/40 bg-destructive/10"
                  : "border-primary/30 bg-primary/10",
            )}
          >
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ background: roleColor(s.role.slug) }}
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-semibold text-foreground">{s.role.name}</div>
              <div className="text-[11px] text-muted-foreground">
                {t("access_sim_via_policy", { policy: s.policy.name })}
              </div>
            </div>
            {isDeny ? (
              <X size={13} className={muted ? "text-muted-foreground/60" : "text-destructive"} />
            ) : (
              <Check size={13} className={muted ? "text-muted-foreground/60" : "text-primary"} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
